import api from '@/api/client';
import { API_BASE_URL } from '@/config';

export interface ResourceUtilizationTableOptions {
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  filter_role?: string;
  filter_status?: string;
  filter_search?: string;
}

export interface ReportDisplayOptions {
  sections: Record<string, { visible: boolean; scale_percent?: number }>;
  header?: { show_time?: boolean };
  resource_utilization?: ResourceUtilizationTableOptions;
}

export interface ReportPreferenceResponse {
  template_id: string;
  display_options: ReportDisplayOptions;
  updated_at?: string;
}

export interface ReportPreferencesListResponse {
  preferences: ReportPreferenceResponse[];
}

export interface BatchDefinitionItemSchema {
  template_id: string;
  display_options: ReportDisplayOptions;
  sort_order?: number;
}

export interface BatchDefinitionCreate {
  name: string;
  items: BatchDefinitionItemSchema[];
}

export interface BatchDefinitionItemResponse {
  id: string;
  template_id: string;
  display_options: ReportDisplayOptions;
  sort_order: number;
}

export interface BatchDefinitionResponse {
  id: string;
  name: string;
  items: BatchDefinitionItemResponse[];
  created_at?: string;
  updated_at?: string;
}

export interface BatchDefinitionListResponse {
  definitions: BatchDefinitionResponse[];
}

export interface BatchRunItemRequest {
  template_id: string;
  display_options: ReportDisplayOptions;
}

export interface BatchRunCreate {
  project_id: string;
  batch_definition_id?: string;
  items?: BatchRunItemRequest[];
}

export interface BatchRunItemResponse {
  id: string;
  template_id: string;
  template_name?: string;
  execution_id?: string;
  status: string;
  error_message?: string;
  sort_order: number;
}

export interface BatchRunResponse {
  id: string;
  status: string;
  items: BatchRunItemResponse[];
  created_at?: string;
  completed_at?: string;
  download_url?: string;
}

export const getReportPreferences = async (): Promise<ReportPreferencesListResponse> => {
  const response = await api.get(
    `${API_BASE_URL}/v1/report-preferences`
  );
  return response.data as ReportPreferencesListResponse;
};

export const getReportPreference = async (
  templateId: string
): Promise<ReportPreferenceResponse> => {
  const response = await api.get(
    `${API_BASE_URL}/v1/report-preferences/${encodeURIComponent(templateId)}`
  );
  return response.data as ReportPreferenceResponse;
};

export const upsertReportPreference = async (
  templateId: string,
  displayOptions: ReportDisplayOptions
): Promise<ReportPreferenceResponse> => {
  const response = await api.put(
    `${API_BASE_URL}/v1/report-preferences/${encodeURIComponent(templateId)}`,
    { display_options: displayOptions }
  );
  return response.data as ReportPreferenceResponse;
};

export const getBatchDefinitions = async (): Promise<BatchDefinitionListResponse> => {
  const response = await api.get(
    `${API_BASE_URL}/v1/batch-definitions`
  );
  return response.data as BatchDefinitionListResponse;
};

export const getBatchDefinition = async (
  id: string
): Promise<BatchDefinitionResponse> => {
  const response = await api.get(
    `${API_BASE_URL}/v1/batch-definitions/${id}`
  );
  return response.data as BatchDefinitionResponse;
};

export const createBatchDefinition = async (
  data: BatchDefinitionCreate
): Promise<BatchDefinitionResponse> => {
  const response = await api.post(
    `${API_BASE_URL}/v1/batch-definitions`,
    data
  );
  return response.data as BatchDefinitionResponse;
};

export const updateBatchDefinition = async (
  id: string,
  data: { name?: string; items?: BatchDefinitionItemSchema[] }
): Promise<BatchDefinitionResponse> => {
  const response = await api.put(
    `${API_BASE_URL}/v1/batch-definitions/${id}`,
    data
  );
  return response.data as BatchDefinitionResponse;
};

export const deleteBatchDefinition = async (id: string): Promise<void> => {
  await api.delete(`${API_BASE_URL}/v1/batch-definitions/${id}`);
};

export const startBatchRun = async (
  data: BatchRunCreate
): Promise<BatchRunResponse> => {
  const response = await api.post(
    `${API_BASE_URL}/v1/batch-runs`,
    data
  );
  return response.data as BatchRunResponse;
};

export const getBatchRun = async (id: string): Promise<BatchRunResponse> => {
  const response = await api.get(
    `${API_BASE_URL}/v1/batch-runs/${id}`
  );
  return response.data as BatchRunResponse;
};

export const downloadBatchRunZip = async (id: string): Promise<Blob> => {
  const response = await api.get(`${API_BASE_URL}/v1/batch-runs/${id}/download`, {
    responseType: 'blob',
  });
  return response.data as Blob;
};
